import { authorizedFetch } from "@/utils/fetch";

const baseUrl = import.meta.env.VITE_API_URL;
const baseUrlV1 = `${baseUrl}/api/v1`;
const saveAccessTokenUrl = `${import.meta.env.VITE_API_URL}/api/v1/save-token`;
const saveAppTokenUrl = `${import.meta.env.VITE_API_URL}/api/v1/master-token`;

export type SocialMediaPagesResponse = {
  pages: SocialMediaPage[];
};

export enum SocialMediaPageConnectionStatus {
  CONNECTED = "connected",
  DISCONNECTED = "disconnected",
}

export type SocialMediaConnection = {
  id: number;
  name: string;
};

export type SocialMediaType = {
  id: number;
  type: string;
  connectedPages: SocialMediaPage[];
  socialMediaConnections: SocialMediaConnection[];
};

export type SocialMediaPage = {
  id: string;
  externalId: string;
  socialMediaConnectionId: number;
  socialMediaType: string;
  dateConnected: string;
  tokenValidUntil: string;
  name: string;
};

export type PageIdTokenTuple = {
  pageId: string;
  accessToken: string;
  socialMediaTypeId: number;
};

export type SubmitPageSelectionPayload = {
  userAccessToken: string;
  socialMediaConnectionId: number;
  pages: PageIdTokenTuple[];
};

export type SubmitAppTokenPayload = {
  masterToken: string;
  socialMediaConnectionId: number;
};

export type RefetchSocialDataPayload = {
  pageId: string;
  minutesToFetch: number;
};

export const getSocialMediaTypes = async (): Promise<SocialMediaType[]> => {
  const response = await authorizedFetch(`${baseUrlV1}/social-media-types`);
  const pagesResponse = await response.json();

  // note: we temporarily set the id of the social media page as name.
  // todo: remove once backend provides the page name
  const socialMediaTypes = (pagesResponse.socialMediaTypes as SocialMediaType[]).map((s) => ({
    ...s,
    connectedPages: s.connectedPages?.map(
      (p) =>
        ({
          ...p,
          name: p.name ?? p.externalId,
          socialMediaConnectionId: s.socialMediaConnections[0].id,
        }),
    ),
  }));

  return socialMediaTypes;
};

export const triggerSocialDataRefetch = async (socialMediaConnectionId: number, payload: RefetchSocialDataPayload) => {
  const url = `${baseUrlV1}/social-media-connections/${socialMediaConnectionId}/refresh`;
  await authorizedFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }).catch((err) => {
    console.error(`could not trigger social data refetch -> ${JSON.stringify(err)}`);
    throw err;
  });
};

export const submitPageSelection = async (payload: SubmitPageSelectionPayload) => {
  await authorizedFetch(saveAccessTokenUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }).catch((err) => {
    console.error(`could not submit page selection -> ${JSON.stringify(err)}`);
  });
  return;
};

export const restorePageConnection = async (
  socialMediaConnectionId: number, 
  pageId: string, 
  userAccessToken: string
): Promise<void> => {
  const url = `${baseUrlV1}/social-media-connections/${socialMediaConnectionId}/reconnect`;
  const payload = { pageId, userAccessToken };
  
  console.log('[restorePageConnection] Calling reconnect endpoint:', {
    url,
    socialMediaConnectionId,
    pageId,
    userAccessTokenLength: userAccessToken?.length
  });
  
  await authorizedFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }).catch((err) => {
    console.error(`could not reconnect page -> ${JSON.stringify(err)}`);
    throw err;
  });
};

export const removePage = async (socialMediaConnectionId: number, id: string): Promise<void> => {
  const params = new URLSearchParams();
  params.set("smc", socialMediaConnectionId.toString());
  await authorizedFetch(`${baseUrlV1}/pages/${id}?${params.toString()}`, {
    method: "DELETE",
  }).catch((err) => {
    console.error(`could not delete selected page -> ${JSON.stringify(err)}`);
  });
  return;
};

export const submitAppToken = async (payload: SubmitAppTokenPayload) => {
  await authorizedFetch(saveAppTokenUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }).catch((err) => {
    err.value = err;
    return null;
  });
  return;
};
